import { accessSurfacesAffectedBy } from '@/api/accessMutations'
import { attachDiagnosticsInterceptor } from '@/api/diagnosticsInterceptor'
import { TIMEOUT_DEFAULT_MS } from '@/api/timeouts'
import { trackApiWrite } from '@/lib/analytics'
import { currentRouteScope } from '@/lib/routeScope'
import { captureException } from '@/lib/sentry'
import { isUserOwner } from '@/stores/auth'
import { applyDeploymentGuard } from '@/utils/deploymentGuard'
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import JSONBig from 'json-bigint'
import { type ApiError, getApiErrorMessage } from './apiError'
import { sessionPort } from './sessionPort'

const isAccessDeniedPath = (): boolean => {
	const segments = window.location.pathname.split('/').filter(Boolean)
	return segments[segments.length - 1] === 'access-denied'
}

/**
 * Tenant the URL names, for the `x-bytebeam-tenant` header.
 *
 * Returns '' for org-level routes (no project segment). Callers that legitimately need to attach a
 * tenant header from an org-level page must pass tenantId explicitly via their request config.
 *
 * Also '' on the project access-denied page, which is reached precisely because that tenant is not
 * accessible: sending its header would 403 every request the page makes.
 */
export const getTenantFromUrl = (): string => {
	const { tenantId } = currentRouteScope()
	if (!tenantId || isAccessDeniedPath()) {
		return ''
	}
	return tenantId
}

const redirectToProjectAccessDeniedIf403 = (): boolean => {
	if (typeof window === 'undefined') {
		return false
	}

	const { orgId, tenantId } = currentRouteScope()
	if (!orgId || !tenantId || isAccessDeniedPath()) {
		return false
	}

	sessionPort.onProjectAccessDenied(orgId, tenantId)
	return true
}

const isTenantContext403 = (url: unknown): boolean => {
	if (typeof url !== 'string') {
		return false
	}
	return url.includes('/v3/me')
}

/**
 * Returns the URL tenant, falling back to the org's root tenant on org-level routes.
 * Use for API calls whose resource belongs to the root project when rendered
 * org-level (e.g. metadata fetches).
 */
export const getTenantForApiCall = (): string => {
	return getTenantFromUrl() || sessionPort.getRootTenantId()
}

/** Owner check from persisted auth, so interceptors and non-axios calls share one rule. */
export const isOwnerFromStoredAuth = (): boolean => {
	try {
		const authStr = typeof localStorage !== 'undefined' ? localStorage.getItem('auth-storage') : null
		if (!authStr) {
			return false
		}
		const authData = JSON.parse(authStr)
		return isUserOwner(authData?.state?.user)
	} catch (e) {
		console.error('Failed to parse auth storage for impersonation check:', e)
		return false
	}
}

/**
 * Headers an org-level caller attaches to reach a tenant-scoped endpoint. Org owners are not direct
 * members of sub-tenants, so the tenant header alone yields no role. Project-level callers that must
 * stay under their own RBAC pass `impersonateAdmin` false. Empty at tenant level, where the URL
 * supplies the tenant and the interceptor fills the header.
 */
export const tenantScopedHeaders = (tenantId?: string, impersonateAdmin = true): Record<string, string> =>
	tenantId ? { 'x-bytebeam-tenant': tenantId, ...(impersonateAdmin && { 'x-bytebeam-impersonate': 'admin' }) } : {}

export const tenantScopedConfig = (tenantId?: string, impersonateAdmin = true): AxiosRequestConfig =>
	tenantId ? { headers: tenantScopedHeaders(tenantId, impersonateAdmin) } : {}

/** A plain fetch bypasses the interceptor, so it applies the same tenant fallback and owner rule itself. */
export const fetchTenantHeaders = (tenantId?: string): Record<string, string> =>
	tenantScopedHeaders(tenantId || getTenantFromUrl(), Boolean(tenantId) && isOwnerFromStoredAuth())

/** POSTs JSON with a plain fetch and returns the raw `Response`, so a download can stream its body. */
export const postForStream = (
	path: string,
	body: unknown,
	options?: { signal?: AbortSignal; tenantId?: string },
): Promise<Response> =>
	fetch(`/api${path}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...fetchTenantHeaders(options?.tenantId) },
		body: JSON.stringify(body),
		credentials: 'include',
		signal: options?.signal,
	})

/** Every mutation passes through these clients, so this is the one place that reliably notices one. */
const notifyAccessAffectingMutation = (response: AxiosResponse): AxiosResponse => {
	const surfaces = accessSurfacesAffectedBy(response.config?.method, response.config?.url)
	if (surfaces.length > 0) {
		sessionPort.onAccessAffectingMutation(surfaces)
	}
	trackApiWrite(response.config?.method, response.config?.url)
	return response
}

const createApiClient = (): AxiosInstance => {
	const client = axios.create({
		baseURL: '/api',
		timeout: TIMEOUT_DEFAULT_MS,
		withCredentials: true, // For session-based auth
	})

	// Must run before auth interceptors — auth rewrites 401/403 errors and drops
	// config, hiding them from diagnostics.
	attachDiagnosticsInterceptor(client, 'api')

	// Tenant header is derived strictly from the URL to prevent stale authStore
	// state from leaking into org-level requests. Org-level callers that
	// legitimately need a tenant header must pass it explicitly via config.headers.
	client.interceptors.request.use(
		config => {
			if (applyDeploymentGuard('api', config)) {
				return config
			}

			if (config.headers && config.headers['x-bypass-tenant-interceptor'] === 'true') {
				config.headers['x-bypass-tenant-interceptor'] = undefined
				return config
			}

			const tenant = getTenantFromUrl()
			const hasTenantHeader =
				config.headers && Object.keys(config.headers).some(k => k.toLowerCase() === 'x-bytebeam-tenant')
			if (tenant && !hasTenantHeader) {
				config.headers['x-bytebeam-tenant'] = tenant
			}

			if (config.headers && config.headers['x-bytebeam-impersonate'] === 'admin' && !isOwnerFromStoredAuth()) {
				config.headers['x-bytebeam-impersonate'] = undefined
			}

			return config
		},
		error => Promise.reject(error),
	)

	client.interceptors.response.use(
		response => notifyAccessAffectingMutation(response),
		error => {
			if (error.response?.status === 401) {
				sessionPort.onUnauthorized()
				const errorMessage =
					error.response?.data?.error ?? error.response?.data?.message ?? 'Session expired. Please login again.'
				return Promise.reject({
					message: errorMessage,
					status: 401,
				} as ApiError)
			}
			if (error.response?.status === 403) {
				const isMeUrl = isTenantContext403(error?.config?.url)
				if (isMeUrl && error.config && error.config.headers && error.config.headers['x-bytebeam-tenant']) {
					const newConfig = { ...error.config }
					newConfig.headers = { ...newConfig.headers }
					newConfig.headers['x-bytebeam-tenant'] = undefined
					newConfig.headers['x-bypass-tenant-interceptor'] = 'true'
					return client(newConfig)
				}

				const shouldRedirect = isTenantContext403(error?.config?.url)
				if (shouldRedirect) {
					redirectToProjectAccessDeniedIf403()
				}
				const errorMessage =
					error.response?.data?.error_message ??
					error.response?.data?.error ??
					error.response?.data?.message ??
					'You do not have permission to access this resource.'
				return Promise.reject({
					message: errorMessage,
					status: 403,
				} as ApiError)
			}
			return Promise.reject(error)
		},
	)

	return client
}

// Dedicated client for owner/org-level APIs — bypasses the tenant header interceptor.
export const createOwnerApiClient = (): AxiosInstance => {
	const client = axios.create({
		baseURL: '/api',
		timeout: TIMEOUT_DEFAULT_MS,
		withCredentials: true, // For session-based auth
	})

	attachDiagnosticsInterceptor(client, 'owner')

	client.interceptors.request.use(
		config => {
			applyDeploymentGuard('owner', config)
			return config
		},
		error => Promise.reject(error),
	)

	client.interceptors.response.use(
		response => notifyAccessAffectingMutation(response),
		error => {
			if (error.response?.status === 401) {
				sessionPort.onUnauthorized()
				const errorMessage =
					error.response?.data?.error ?? error.response?.data?.message ?? 'Session expired. Please login again.'
				return Promise.reject({
					message: errorMessage,
					status: 401,
				} as ApiError)
			}
			if (error.response?.status === 403) {
				const shouldRedirect = isTenantContext403(error?.config?.url)
				if (shouldRedirect) {
					redirectToProjectAccessDeniedIf403()
				}
				const errorMessage =
					error.response?.data?.error_message ??
					error.response?.data?.error ??
					error.response?.data?.message ??
					'You do not have permission to access this resource.'
				return Promise.reject({
					message: errorMessage,
					status: 403,
				} as ApiError)
			}
			return Promise.reject(error)
		},
	)

	return client
}

// Axios instance for console APIs at console.bytebeam.io.
export const createConsoleApiClient = (): AxiosInstance => {
	const client = axios.create({
		baseURL: '/api',
		timeout: TIMEOUT_DEFAULT_MS,
		withCredentials: true,
	})

	attachDiagnosticsInterceptor(client, 'console')

	client.interceptors.request.use(
		config => {
			// 'console' client is for billing/plans APIs - allowed on Console
			applyDeploymentGuard('console', config)
			return config
		},
		error => Promise.reject(error),
	)

	client.interceptors.response.use(
		response => notifyAccessAffectingMutation(response),
		error => {
			if (error.response?.status === 401) {
				sessionPort.onUnauthorized()
				const errorMessage =
					error.response?.data?.error ?? error.response?.data?.message ?? 'Session expired. Please login again.'
				return Promise.reject({
					message: errorMessage,
					status: 401,
				} as ApiError)
			}
			if (error.response?.status === 403) {
				const errorMessage =
					error.response?.data?.error_message ??
					error.response?.data?.error ??
					error.response?.data?.message ??
					'Forbidden, you are not authorized to access this resource.'
				return Promise.reject({
					message: errorMessage,
					status: 403,
				} as ApiError)
			}
			return Promise.reject(error)
		},
	)

	return client
}

export const apiClient = createApiClient()
export const ownerApiClient = createOwnerApiClient()
export const consoleApiClient = createConsoleApiClient()

const buildApiError = (error: unknown): ApiError => {
	if (axios.isAxiosError(error)) {
		if (
			typeof error.response?.data === 'string' &&
			error.response.data.trim() &&
			!error.response.data.trim().startsWith('<')
		) {
			return {
				message: error.response.data.trim(),
				status: error.response.status ?? 500,
				code: error.code,
				dependencies: undefined,
			}
		}

		return {
			message: getApiErrorMessage(error) ?? error.message ?? 'An unexpected error occurred',
			status: error.response?.status ?? 500,
			// Fall back to the axios transport code (e.g. ECONNABORTED on timeout) so
			// callers can distinguish "request aborted client-side" from a backend failure.
			code: error.response?.data?.code ?? error.code,
			dependencies: error.response?.data?.dependencies,
		}
	}

	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		'status' in error &&
		typeof (error as ApiError).message === 'string' &&
		typeof (error as ApiError).status === 'number'
	) {
		return error as ApiError
	}

	return {
		message: 'A non-network error occurred',
		status: 500,
	} as ApiError
}

export const mapApiError = (error: unknown): ApiError => {
	const apiError = buildApiError(error)
	// Routine 401/403 keep the status the interceptor gave them, and a cancelled, timed out or
	// unreachable request never got a response, so only an actual server or client failure is reported.
	const noResponse = axios.isAxiosError(error) && !error.response
	if (apiError.status >= 500 && !noResponse) {
		const request = axios.isAxiosError(error) ? error.config : undefined
		captureException(error, {
			tags: { status: apiError.status, method: request?.method, url: request?.url },
			extra: { message: apiError.message, code: apiError.code },
		})
	}
	return apiError
}

/**
 * True when the request was aborted client-side by the axios timeout — the backend
 * may still be processing it, so callers should not treat this as a hard failure.
 *
 * ECONNABORTED ("connection aborted"): axios killed the request itself after
 * `timeout` ms with no response. ETIMEDOUT: the same condition reported at the
 * OS socket level (some axios/Node versions use this code instead). Both mean
 * "we gave up waiting" — unlike ERR_NETWORK (connection broke) or an HTTP error
 * status (server responded with a failure).
 */
export const isTimeoutApiError = (error: unknown): boolean => {
	const code = (error as { code?: string } | null)?.code
	return code === 'ECONNABORTED' || code === 'ETIMEDOUT'
}

export const apiCall = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	try {
		const response: AxiosResponse<T> = await apiClient(url, config)
		return response.data
	} catch (error: unknown) {
		throw mapApiError(error)
	}
}

export const ownerApiCall = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	try {
		const response: AxiosResponse<T> = await ownerApiClient(url, config)
		return response.data
	} catch (error: unknown) {
		throw mapApiError(error)
	}
}

export const consoleApiCall = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	try {
		const response: AxiosResponse<T> = await consoleApiClient(url, config)
		return response.data
	} catch (error: unknown) {
		throw mapApiError(error)
	}
}

if (typeof BigInt !== 'undefined' && !('toJSON' in BigInt.prototype)) {
	/**
	 * Global BigInt serialization polyfill to prevent JSON.stringify crashes.
	 * This ensures that if any BigInt values bleed into other modules,
	 * they are safely serialized as strings rather than throwing a TypeError.
	 */
	Object.defineProperty(BigInt.prototype, 'toJSON', {
		value: function () {
			return this.toString()
		},
		configurable: true,
		writable: true,
	})
}

/**
 * Specialized JSON parser for Dashboard data that handles 64-bit integers.
 * - useNativeBigInt: true -> Captures large IDs/timestamps as BigInt.
 * - alwaysParseAsBig: false -> Preserves high-precision floats (Coordinates) as Numbers.
 * - storeAsString: false -> Prevents unintended float-to-string conversion.
 */
const jsonBigParser = JSONBig({
	useNativeBigInt: true,
	alwaysParseAsBig: false,
	storeAsString: false,
})

/**
 * specialized version of apiCall that uses json-bigint for response parsing.
 * Use for any endpoint returning BIGINT/large numeric fields (e.g. Dashboard/Panel data,
 * asset ids) — plain JSON.parse silently loses precision on those. Convert BigInt fields to
 * string immediately after the call rather than passing them further to avoid breaking strict
 * equality against plain numbers/strings elsewhere in the app.
 */
export const apiCallWithBigInt = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	const bigIntConfig: AxiosRequestConfig = {
		...config,
		transformResponse: [
			data => {
				if (typeof data !== 'string' || data === '') {
					return data
				}
				try {
					return jsonBigParser.parse(data)
				} catch {
					// Silent fallback to standard JSON.parse
					// This is expected when response contains decimal numbers (BigInt only supports integers)
					return JSON.parse(data)
				}
			},
		],
	}
	return apiCall(url, bigIntConfig)
}
